"use client";

import Link from "next/link";

function formatPricePi(pricePi) {
  if (pricePi === undefined || pricePi === null) return "—"; // don't lie
  const n = Number(pricePi);
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return "Free";
  if (n < 0) return "—";
  return `${n} π`;
}

function formatPriceUsd(priceUsd) {
  if (priceUsd === undefined || priceUsd === null) return null;
  const n = Number(priceUsd);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `$${n.toFixed(2)} USDC`;
}

function EbookCard({ book, variant = "list", disableLink = false }) {
  const slug = book?.slug;

  // 🔍 DEBUG ONLY – remove after fixing
 // if (variant === "grid" && book?.slug === "magna-carta-a-commentary-on-the-great-charter-of-king-john-william-sharp-mckechnie-snx6") {
   // console.log("PRICE DEBUG", {
     // slug: book.slug,
     // pricePi: book.pricePi,
     // priceUsd: book.priceUsd,
     // pricePiType: typeof book.pricePi,
     // priceUsdType: typeof book.priceUsd,
   // });
  //}

  const realAuthors =
    Array.isArray(book?.authors) && book.authors.length
      ? book.authors.join(", ")
      : null;

  const uploader = book?.authorName || "Unknown";
  const category = book?.category || "Other";
  const priceLabel = formatPricePi(book?.pricePi);
const usdLabel = formatPriceUsd(book?.priceUsd);
const hasUsd = !!usdLabel;

  const href = slug ? `/ebooks/${slug}` : null;

  // =========================
  // GRID (Kindle-style tiles)
  // =========================
  
  if (variant === "grid") {
    const GridInner = (
      <div
        className="relative group rounded-2xl border border-border bg-surface/40 p-3 transition
                   hover:bg-surface/60 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/30"
      >
        
        {/* Cover */}
        <div className="relative overflow-hidden rounded-xl bg-background/40 border border-border">
          <div className="aspect-[2/3] max-h-56 ">  {/* cap height */}
            {book?.coverUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={book.coverUrl}
                alt={book?.title || "Ebook cover"}
                className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]"
                loading="lazy"
              />
            ) : (
              <div className="h-full w-full" />
            )}
          </div>

          {/* subtle “shine” */}
          <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition">
            <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
          </div>

          {/* Price badge */}
          <div className="absolute bottom-2 right-2 rounded-full bg-background/80 px-2 py-1 text-[11px] font-semibold border border-border">
            {priceLabel}
          </div>
          {hasUsd && (
  <div className="absolute bottom-2 left-2 rounded-full bg-emerald-900/50 px-2 py-1 text-[11px] font-semibold border border-emerald-500/30">
    {usdLabel ?? "USDC"}
  </div>
)}
        </div>

        {/* Meta */}
        <div className="mt-3 space-y-1">
          <h3 className="text-sm font-semibold leading-snug line-clamp-2">
            {book?.title || "Untitled"}
          </h3>

          <p className="text-xs text-textSecondary line-clamp-1">
  {realAuthors && <span>by {realAuthors}</span>}
</p>

<p className="text-[11px] text-textSecondary/80">
  uploaded by <span className="font-medium text-textSecondary">{uploader}</span>
</p>


          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-[11px] inline-flex items-center rounded-full border border-border bg-background/30 px-2 py-0.5 text-textSecondary">
              {category}
            </span>

            <span className="text-[11px] text-textSecondary group-hover:text-textPrimary transition">
              View →
            </span>
          </div>

          {/* Stats (hover-only) */}
          <div className="mt-2 text-[11px] text-textSecondary opacity-0 group-hover:opacity-100 transition flex items-center gap-3">
            <span>👁 {book?.views ?? 0}</span>
            <span>🛒 {book?.purchasesCount ?? 0}</span>
          </div>
        </div>
      </div>
    );

    return href && !disableLink ? (
      <Link href={href} className="block">
        {GridInner}
      </Link>
    ) : (
      GridInner
    );
  }

  // =========================
  // LIST (your current layout)
  // =========================
  const ListInner = (
    <div className="flex gap-3 rounded-lg border border-gray-700 bg-black/20 p-3 text-textPrimary hover:bg-black/30 transition">
      {/* Cover */}
      <div className="flex-shrink-0 w-14">
        <div className="w-14 h-20 rounded-md overflow-hidden bg-black/30 border border-white/10">
          {book?.coverUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={book.coverUrl}
              alt={book?.title || "Ebook cover"}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : null}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold leading-tight line-clamp-2">
              {book?.title || "Untitled"}
            </h3>

      <p className="mt-0.5 text-xs text-textSecondary line-clamp-1">
  {realAuthors && <span>by {realAuthors} · </span>}
 <span className="font-medium hover:underline cursor-pointer">
  {uploader}
</span>

</p>

          </div>
<div className="shrink-0 text-right">
  <div className="text-sm font-semibold opacity-90">{priceLabel}</div>
  {hasUsd && (
    <div className="mt-0.5 text-[11px] text-emerald-200/90">
      {usdLabel}
    </div>
  )}
</div>

        </div>

        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] opacity-80">
            {category}
          </span>

          {/* Stats (always visible in list) */}
          <div className="flex items-center gap-3 text-[11px] text-textSecondary">
            <span>👁 {book?.views ?? 0}</span>
            <span>🛒 {book?.purchasesCount ?? 0}</span>
          </div>
        </div>

        {book?.description ? (
          <p className="mt-2 text-sm opacity-80 leading-snug line-clamp-2">
            {book.description}
          </p>
        ) : null}
      </div>
    </div>
  );

return href && !disableLink ? (
  <Link href={href} className="block">{ListInner}</Link>
) : (
  ListInner
);

}

export default EbookCard;
